import React, { useEffect } from 'react'
import { X } from 'lucide-react'
import { LogTerminal } from './LogTerminal'
import { StatsPanel } from './StatsPanel'

/**
 * ObservabilityModal — overlay that hosts the live log terminal and the
 * stats sparkline panel for one tenant domain.
 *
 * Layout: stats on top (compact, fixed-height), terminal underneath
 * filling the remaining vertical space. The terminal is the gravity well
 * of the modal — sparklines are an at-a-glance health header.
 *
 * Lifecycle: streams open on mount via the children's own useEffects,
 * and close on unmount. Closing the modal therefore frees both server-
 * side stream slots (relevant for the 4-concurrent cap).
 */

interface ObservabilityModalProps {
  domainId: number
  domainName: string
  onClose: () => void
}

export const ObservabilityModal: React.FC<ObservabilityModalProps> = ({
  domainId,
  domainName,
  onClose,
}) => {
  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="observability-modal-title"
        data-testid="observability-modal"
        className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 w-full max-w-5xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-800">
          <h2
            id="observability-modal-title"
            className="text-base font-medium text-gray-900 dark:text-gray-100 truncate"
          >
            Observability — {domainName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-md grid place-items-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close observability"
            data-testid="observability-modal-close"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Stats row */}
        <div className="border-b border-gray-200 dark:border-gray-800">
          <StatsPanel domainId={domainId} />
        </div>

        {/* Log terminal — fills remaining height. */}
        <div className="flex-1 min-h-0">
          <LogTerminal domainId={domainId} domainName={domainName} />
        </div>
      </div>
    </div>
  )
}

export default ObservabilityModal
